import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Check, Phone, Mail, Calendar, MessageCircle } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCER_PORTRAIT = "/media/producer-portrait.jpg";

const CONTACT_ACTIONS = [
  {
    label: "Call",
    value: "+91 92586 51242",
    href: "tel:+919258651242",
    Icon: Phone,
    tid: "connect-call",
  },
  {
    label: "WhatsApp",
    value: "Message anytime",
    href: "https://wa.me/919258651242",
    Icon: MessageCircle,
    external: true,
    tid: "connect-whatsapp",
  },
  {
    label: "Email",
    value: "ida@brandnewmoneyfilms.com",
    href: "mailto:ida@brandnewmoneyfilms.com",
    Icon: Mail,
    tid: "connect-email-action",
  },
  {
    label: "Schedule a meeting",
    value: "Pick a time that works for you",
    href: "mailto:ida@brandnewmoneyfilms.com?subject=Meeting%20with%20Your%20Producer&body=Hi%2C%20I%27d%20love%20to%20schedule%20a%20meeting%20to%20talk%20about%20a%20project.",
    Icon: Calendar,
    tid: "connect-schedule",
  },
];

export default function Connect() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (sending || done || !form.email) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/quote`, {
        blueprint_id: "connect-direct",
        ...form,
      });
      setSuccessMsg(data?.message || "");
      setDone(true);
    } catch (err) {
      console.error(err);
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-ink flex flex-col">
      <Nav />

      <main className="flex-1">
        <section className="max-w-[1280px] mx-auto px-6 md:px-10 pt-20 md:pt-32 pb-10 md:pb-14">
          <Reveal>
            <div className="eyebrow eyebrow-denim mb-8">Connect</div>
          </Reveal>
          <Reveal as="h1" delay={80} className="font-display font-extrabold text-[44px] sm:text-[64px] md:text-[88px] tracking-[-0.045em] leading-[0.96] text-ink max-w-[1100px]">
            Your <span className="font-serif-italic font-light">vision</span>
            <br />
            starts <span className="font-serif-italic bnm-denim-shine font-light">here</span>.
          </Reveal>
          <Reveal delay={180}>
            <p className="text-muted-ink text-[16px] md:text-[18px] mt-7 max-w-[640px] leading-[1.7]">
              This is not a contact form. It{"\u2019"}s the beginning of a partnership.
              Meet the person who will carry your idea from the first conversation
              to the final delivery.
            </p>
          </Reveal>
        </section>

        <section className="max-w-[1280px] mx-auto px-6 md:px-10 pb-24 md:pb-36">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
            {/* Meet Your Producer */}
            <aside className="lg:col-span-5 flex flex-col gap-5">
              <Reveal>
                <div
                  className="bnm-card overflow-hidden"
                  data-testid="connect-producer-card"
                >
                  <div className="relative aspect-[5/6] bg-[#f4f4f4] overflow-hidden">
                    <img
                      src={PRODUCER_PORTRAIT}
                      alt="Your Producer at Brand New Money Films"
                      loading="lazy"
                      className="w-full h-full object-cover object-[center_20%] transition-transform duration-[900ms] ease-out hover:scale-[1.02]"
                      data-testid="connect-producer-portrait"
                    />
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-0 mix-blend-multiply opacity-[0.05]"
                      style={{
                        backgroundImage:
                          'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22160%22 height=%22160%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 stitchTiles=%22stitch%22/></filter><rect width=%22100%22 height=%22100%22 filter=%22url(%23n)%22/></svg>")',
                      }}
                    />
                  </div>

                  <div className="px-7 md:px-8 pt-7 md:pt-8 pb-7 md:pb-8">
                    <div
                      className="font-serif-italic text-muted-ink/80 text-[28px] md:text-[32px] leading-none tracking-[-0.02em]"
                      style={{ fontFamily: "'Fraunces', serif" }}
                      data-testid="connect-producer-signature"
                    >
                      Div
                    </div>

                    <div
                      className="font-display font-extrabold text-ink text-[28px] md:text-[32px] tracking-[-0.025em] leading-[1.1] mt-5"
                      data-testid="connect-producer-name"
                    >
                      Your <span className="font-serif-italic font-light">Producer</span>
                    </div>

                    <div
                      className="mt-3 inline-flex items-center gap-2 text-[12.5px] text-muted-ink"
                      data-testid="connect-producer-status"
                      aria-live="polite"
                    >
                      <span className="relative flex items-center justify-center w-2 h-2">
                        <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-500 opacity-40 animate-ping" />
                        <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" />
                      </span>
                      <span className="whitespace-nowrap">Available for new projects</span>
                    </div>

                    <p className="text-ink text-[16.5px] md:text-[17.5px] mt-6 leading-[1.6] font-medium" data-testid="connect-producer-welcome">
                      Let{"\u2019"}s build something unforgettable.
                    </p>
                    <p className="text-muted-ink text-[14.5px] md:text-[15px] mt-3 leading-[1.7]">
                      Every project here starts with a real conversation. Tell me
                      what you{"\u2019"}re imagining and I{"\u2019"}ll help shape the creative,
                      the plan and the production around it.
                    </p>

                    {/* Ways to reach Your Producer */}
                    <div className="mt-7 flex flex-col" data-testid="connect-actions">
                      {CONTACT_ACTIONS.map((a, i) => (
                        <a
                          key={a.label}
                          href={a.href}
                          {...(a.external ? { target: "_blank", rel: "noreferrer" } : {})}
                          className={`group flex items-center gap-4 py-3.5 ${i > 0 ? "border-t border-bnm" : ""} transition-colors`}
                          data-testid={a.tid}
                        >
                          <span className="w-10 h-10 rounded-full border border-bnm text-ink flex items-center justify-center shrink-0 transition-colors group-hover:border-black">
                            <a.Icon className="w-4 h-4" strokeWidth={1.6} aria-hidden />
                          </span>
                          <span className="flex flex-col min-w-0">
                            <span className="text-[14.5px] font-semibold text-ink leading-[1.3]">{a.label}</span>
                            <span className="text-[12.5px] text-muted-ink truncate">{a.value}</span>
                          </span>
                          <ArrowRight className="w-4 h-4 text-muted-ink/50 ml-auto shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-ink" aria-hidden />
                        </a>
                      ))}
                    </div>
                    <p className="mt-4 text-[11.5px] text-muted-ink leading-[1.6]" data-testid="connect-secondary-email">
                      Also reachable at{" "}
                      <a href="mailto:brandnewmoneyfilms@gmail.com" className="underline underline-offset-2 hover:text-ink transition-colors">
                        brandnewmoneyfilms@gmail.com
                      </a>
                    </p>
                  </div>
                </div>
              </Reveal>
            </aside>

            {/* Write to Your Producer */}
            <Reveal className="lg:col-span-7">
              <div className="bnm-card p-7 md:p-10">
                {done ? (
                  <div data-testid="connect-success">
                    <div className="w-12 h-12 rounded-full bg-denim-bnm/10 text-denim-bnm flex items-center justify-center mb-5">
                      <Check className="w-6 h-6" />
                    </div>
                    <p className="font-display text-[26px] md:text-[30px] tracking-[-0.025em] text-ink leading-[1.2] mb-3">
                      Thank you.
                    </p>
                    <p className="text-muted-ink text-[15px] leading-[1.7] max-w-[480px]">
                      {successMsg ||
                        "Your Producer at BRAND NEW MONEY FILMS is reviewing your request. We'll be in touch shortly."}
                    </p>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-5">
                    <div>
                      <h2 className="font-display font-extrabold text-[24px] md:text-[28px] tracking-[-0.025em] leading-[1.15] text-ink">
                        Or write to me <span className="font-serif-italic font-light">here</span>.
                      </h2>
                      <p className="text-muted-ink text-[14px] mt-2 leading-[1.6]">
                        A few lines are enough. I read every message personally.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="eyebrow eyebrow-muted block mb-2">Name</label>
                        <input
                          type="text"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="Your full name"
                          className="w-full border border-bnm rounded-xl px-4 py-3 text-[15px] outline-none focus:border-black transition-colors"
                          data-testid="connect-name"
                        />
                      </div>
                      <div>
                        <label className="eyebrow eyebrow-muted block mb-2">Email</label>
                        <input
                          type="email"
                          required
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          placeholder="you@studio.com"
                          className="w-full border border-bnm rounded-xl px-4 py-3 text-[15px] outline-none focus:border-black transition-colors"
                          data-testid="connect-email"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="eyebrow eyebrow-muted block mb-2">Phone (+91)</label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="+91 XXXXX XXXXX"
                        className="w-full border border-bnm rounded-xl px-4 py-3 text-[15px] outline-none focus:border-black transition-colors"
                        data-testid="connect-phone"
                      />
                    </div>
                    <div>
                      <label className="eyebrow eyebrow-muted block mb-2">Describe your vision</label>
                      <textarea
                        rows={4}
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        placeholder="One sentence is enough."
                        className="w-full border border-bnm rounded-xl px-4 py-3 text-[15px] outline-none focus:border-black transition-colors resize-none"
                        data-testid="connect-notes"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={sending}
                      className="btn-denim w-full md:w-auto"
                      data-testid="connect-submit"
                    >
                      <span>{sending ? "Sending…" : "Send to Your Producer"}</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                    <p className="text-[12.5px] text-muted-ink italic pt-1">
                      From first conversation to final delivery.
                    </p>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
